import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  Stack,
  Typography,
  Theme,
  alpha,
  Divider,
  Grid,
  Tooltip
} from "@mui/material";
import {
  ChevronRight as ChevronRightIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { useSessionQuery } from "../../auth";
import { navigationSections } from "../../../shared/config/navigationConfig";
import { useCapabilities } from "../../../hooks/useCapabilities";
import { filterNavigationItems } from "../../../shared/config/navAccess";

export const Dashboard: React.FC = () => {

  const navigate = useNavigate();

  const { canRoute } = useCapabilities();
  const { data: session } = useSessionQuery();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 2 } }}>
      <Stack spacing={2} sx={{ pb: 4 }}>
        <Box>
          <Typography variant="h6">
            Welcome back, {session?.user?.first_name} 👋
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Your central workspace for performance, oversight, collaboration,
            risk and administration
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {filterNavigationItems(navigationSections, canRoute).map((section) => (
            <Grid
              size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}
              key={section.title}
            >
              <Card
                key={section.title}
                variant="outlined"
                sx={{ height: "100%" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      py: 1.5,
                      px: 2,
                      display: "flex",
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: section.iconColor,
                        fontSize: 40,
                        width: { xs: 40, md: 36, xl: 40 },
                        height: { xs: 40, md: 36, xl: 40 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: 1,
                        flexShrink: 0,
                        color: (theme: Theme) =>
                          theme.palette.primary.contrastText,
                      }}
                    >
                      {section.icon}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: -1,
                        width: "100%",
                        overflow: "hidden",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {section.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {section.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider flexItem />
                  <Grid container spacing={0.5} sx={{ p: 1.5 }}>
                    {section.items.filter(item => !item.isHiddenInNavigation).map((item) => {
                      return (
                        <Grid size={{ xs: 12, sm: 12, md: 12 }} key={item.label}>
                          <Box
                            onClick={() => {
                              if (item.comingSoon) return;
                              handleNavigate(item.path);
                            }}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              width: "100%",
                              overflow: "hidden",
                              py: { xs: 1, md: 0.75, xl: 1.2 },
                              px: 1.5,
                              borderRadius: 1,
                              backgroundColor: "action.hover",
                              color: "text.secondary",
                              cursor: !item.comingSoon ? "pointer" : "default",
                              "&:hover": {
                                backgroundColor:
                                  !item.comingSoon ? (theme: Theme) =>
                                    alpha(theme.palette.primary.main, 0.15)
                                    : "action.hover",
                                color:
                                  !item.comingSoon ? (theme: Theme) => theme.palette.primary.main
                                    : "text.secondary",
                              },
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                              flex={1}
                              overflow="hidden"
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {item.icon}
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  flex: 1,
                                  textOverflow: "ellipsis",
                                  width: "100%",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                }}
                              >
                                {item.label}
                              </Typography>
                            </Stack>
                            {/* RIGHT ICON */}
                            {item.comingSoon && (
                              <Tooltip title="Coming Soon">
                                <InfoOutlinedIcon fontSize="small" opacity={0.5} />
                              </Tooltip>
                            )}

                            {!item.comingSoon && (
                              <ChevronRightIcon opacity={0.5} />
                            )}
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Stack>
    </Box>
  );
};
